import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import axios from "axios";
import {
  FacebookShareButton,
  FacebookIcon,
  LinkedinShareButton,
  LinkedinIcon,
  PinterestShareButton,
  PinterestIcon,
  TwitterShareButton,
  TwitterIcon,
  WhatsappShareButton,
  WhatsappIcon,
} from "react-share";

function getImageUrl(image) {
  if (!image) return "";
  if (image.startsWith("http://") || image.startsWith("https://")) return image;
  return `${process.env.REACT_APP_ASSET_URL}/${image}`;
}

export const ProductPage = ({
  fetchProduct,
  isLoading,
  product,
  addToCart,
  updateCart,
  cart,
  addToWishlist,
  removeToWishlist,
  isAdded,
  isLoggedIn,
}) => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [cartPr, setCartPr] = useState({});
  const [localQty, setLocalQty] = useState(1);
  const [activeTab, setActiveTab] = useState("description");
  const [activeImage, setActiveImage] = useState(null);
  const [lightbox, setLightbox] = useState(null);
  const [zoomed, setZoomed] = useState(false);
  const [zoomPos, setZoomPos] = useState({ x: 50, y: 50 });

  const benefits = (() => {
    if (!product?.benefits) return [];
    if (Array.isArray(product.benefits)) return product.benefits;
    try {
      const parsed = JSON.parse(product.benefits);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  })();

  const extraImages = Array.isArray(product?.images) ? product.images : [];
  const mainImageUrl = getImageUrl(product?.image);

  const [reviews, setReviews] = useState([]);

  useEffect(() => {
    if (product?.id) {
      axios.get(`${process.env.REACT_APP_API_URL}/review/product/${product.id}`)
        .then(res => {
          setReviews(res.data.data || []);
        })
        .catch(err => console.error("Error fetching reviews:", err));
    }
  }, [product?.id]);

  const totalReviews = reviews.length;
  const averageRating = totalReviews > 0 
    ? (reviews.reduce((acc, curr) => acc + curr.rating, 0) / totalReviews).toFixed(1) 
    : 0;

  const renderStars = (rating) => {
    const num = Math.max(0, Math.min(5, isNaN(Number(rating)) ? 0 : Math.round(Number(rating))));
    return "★".repeat(num) + "☆".repeat(5 - num);
  };

  // All images: main first, then extras
  const allImages = [
    ...(product?.image ? [{ id: "main", url: mainImageUrl }] : []),
    ...extraImages.map((img) => ({ id: img.id, url: getImageUrl(img.image) })),
  ];

  useEffect(() => {
    if (slug) fetchProduct(slug);
  }, [slug]);
  useEffect(() => {
    if (isAdded) fetchProduct(slug);
  }, [isAdded]);

  // Reset active image when product changes
  useEffect(() => {
    if (product?.image) {
      setActiveImage(getImageUrl(product.image));
    } else if (extraImages.length > 0) {
      setActiveImage(getImageUrl(extraImages[0].image));
    }
  }, [product?.id, product?.image]);

  useEffect(() => {
    if (product?.id) {
      const c = cart.filter((x) => x.ProductId == product.id);
      setCartPr(c.length > 0 ? c[0] : {});
    } else {
      setCartPr({});
    }
  }, [cart, product]);

  useEffect(() => {
    setLocalQty(1);
  }, [product?.id]);

  // Zoom mouse move handler
  const handleMouseMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    setZoomPos({ x, y });
  };

  if (isLoading) {
    return (
      <div className="container" style={{ marginTop: 80, textAlign: "center" }}>
        <div className="spinner-border" role="status" />
      </div>
    );
  }

  const displayImage = activeImage || mainImageUrl;

  return (
    <div className="container" style={{ marginTop: "40px" }}>
      <div className="row">
        {/* ===== Left — Image viewer + thumbnails ===== */}
        <div className="col-md-6">
          {/* ✅ Main image with CSS zoom on hover */}
          <div
            style={{
              width: "100%",
              border: "1px solid #dadada",
              padding: "20px",
              borderRadius: 8,
              overflow: "hidden",
              position: "relative",
              cursor: zoomed ? "zoom-out" : "zoom-in",
              backgroundColor: "#fff",
            }}
            onMouseEnter={() => setZoomed(true)}
            onMouseLeave={() => {
              setZoomed(false);
              setZoomPos({ x: 50, y: 50 });
            }}
            onMouseMove={handleMouseMove}
            onClick={() => setLightbox(displayImage)}
          >
            {displayImage ? (
              <img
                src={displayImage}
                alt="product"
                style={{
                  width: "100%",
                  height: "420px",
                  objectFit: "contain",
                  display: "block",
                  transformOrigin: `${zoomPos.x}% ${zoomPos.y}%`,
                  transform: zoomed ? "scale(2)" : "scale(1)",
                  transition: zoomed ? "none" : "transform 0.2s ease",
                  pointerEvents: "none",
                }}
              />
            ) : (
              <div
                style={{
                  height: 300,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#ccc",
                }}
              >
                No image
              </div>
            )}
          </div>

          {/* ✅ Thumbnail strip */}
          {allImages.length > 1 && (
            <div
              style={{
                display: "flex",
                gap: 8,
                marginTop: 12,
                flexWrap: "wrap",
              }}
            >
              {allImages.map((img) => (
                <div
                  key={img.id}
                  onClick={() => setActiveImage(img.url)}
                  style={{
                    width: 68,
                    height: 68,
                    border:
                      displayImage === img.url
                        ? "2px solid #66A96B"
                        : "2px solid #e0e0e0",
                    borderRadius: 6,
                    overflow: "hidden",
                    cursor: "pointer",
                    flexShrink: 0,
                    transition: "border-color 0.15s",
                  }}
                >
                  <img
                    src={img.url}
                    alt="thumbnail"
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      display: "block",
                    }}
                  />
                </div>
              ))}
            </div>
          )}

          {allImages.length > 1 && (
            <p style={{ fontSize: 12, color: "#aaa", marginTop: 6 }}>
              Click thumbnail to switch · Hover main image to zoom · Click to
              enlarge
            </p>
          )}
        </div>

        {/* ===== Right — Product info ===== */}
        <div className="col-md-6">
          <div className="product-detail-container">
            <h1
              style={{
                fontWeight: 700,
                textTransform: "uppercase",
                fontSize: 24,
                marginBottom: 4
              }}
            >
              {product?.name}
            </h1>

            {totalReviews > 0 && (
              <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 12 }}>
                <div style={{ color: "#FFD700", fontSize: 18 }}>
                  {renderStars(averageRating)}
                </div>
                <span style={{ fontSize: 14, color: "#555", fontWeight: 600 }}>
                  {averageRating} / 5.0
                </span>
                <span style={{ fontSize: 13, color: "gray" }}>
                  ({totalReviews} {totalReviews === 1 ? "review" : "reviews"})
                </span>
              </div>
            )}

            {/* Price */}
            <div style={{ margin: "12px 0" }}>
              <h4
                style={{ color: "#66A96B", fontWeight: 700, marginBottom: 4 }}
              >
                ₹ {product?.currentPrice}
              </h4>
              {product?.mrp &&
                Number(product.mrp) > Number(product.currentPrice) && (
                  <>
                    <span
                      style={{
                        color: "gray",
                        textDecoration: "line-through",
                        fontSize: 14,
                      }}
                    >
                      MRP: ₹ {product?.mrp}
                    </span>
                    <span
                      style={{
                        marginLeft: 8,
                        background: "#66A96B",
                        color: "white",
                        fontSize: 12,
                        padding: "2px 8px",
                        borderRadius: 4,
                      }}
                    >
                      {Math.round(
                        ((product.mrp - product.currentPrice) / product.mrp) *
                          100,
                      )}
                      % OFF
                    </span>
                  </>
                )}
            </div>

            <h4 style={{ color: "#66A96B", fontWeight: 700, fontSize: 15 }}>
              ✓ In Stock
            </h4>

            <p style={{ fontWeight: 500, fontSize: 14, color: "gray" }}>
              <i className="fa-solid fa-tag" style={{ marginRight: 6 }}></i>
              SKU: <strong style={{ color: "#333" }}>{product?.sku}</strong>
            </p>

            <p style={{ fontSize: 13, color: "gray" }}>
              GST: {product?.gst}% &nbsp;|&nbsp; HSN: {product?.hsn}
            </p>

            {/* Benefits */}
            {benefits.length > 0 && (
              <div style={{ margin: "16px 0" }}>
                <strong style={{ fontSize: 14 }}>Key Benefits:</strong>
                <ul style={{ marginTop: 8, paddingLeft: 20 }}>
                  {benefits.map((b, i) => (
                    <li
                      key={i}
                      style={{ fontSize: 14, color: "#444", marginBottom: 4 }}
                    >
                      {b}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {product?.tags && (
              <p style={{ fontSize: 13, color: "gray" }}>
                Tags: {product.tags}
              </p>
            )}

            {/* Quantity + Cart */}
            <div
              className="quantity-maincontainer mg"
              style={{ margin: "20px 0" }}
            >
              <div className="quantity-container">
                <button
                  className="quantity-button"
                  onClick={() => {
                    if (cartPr?.id) {
                      if (cartPr.quantity > 1) {
                        updateCart({
                          id: cartPr.id,
                          quantity: parseInt(cartPr.quantity) - 1,
                        });
                      }
                    } else {
                      setLocalQty((prev) => Math.max(1, prev - 1));
                    }
                  }}
                >
                  -
                </button>
                <input
                  type="number"
                  className="quantity-input"
                  value={cartPr?.id ? (cartPr.quantity || 1) : localQty}
                  readOnly
                  min="1"
                />
                <button
                  className="quantity-button"
                  onClick={() => {
                    if (cartPr?.id) {
                      if (cartPr.quantity < 10) {
                        updateCart({
                          id: cartPr.id,
                          quantity: parseInt(cartPr.quantity) + 1,
                        });
                      }
                    } else {
                      setLocalQty((prev) => Math.min(10, prev + 1));
                    }
                  }}
                >
                  +
                </button>
              </div>
              <div
                className="button-container"
                style={{ marginTop: 12, display: "flex", gap: 10 }}
              >
                <button
                  className="button ac-button"
                  onClick={() => {
                    if (cartPr?.id) {
                      if ((parseInt(cartPr.quantity) || 0) < 10) {
                        addToCart({ quantity: 1, ProductId: product.id });
                      } else {
                        toast.info("Maximum quantity limit of 10 reached.");
                      }
                    } else {
                      addToCart({ quantity: localQty, ProductId: product.id });
                    }
                  }}
                >
                  Add to Cart
                </button>
                <button
                  className="button ac-button"
                  onClick={() => {
                    if (!cartPr?.id) {
                      addToCart({ quantity: localQty, ProductId: product.id });
                    }
                    navigate("/checkout");
                  }}
                >
                  Buy Now
                </button>
              </div>
            </div>

            {/* Wishlist */}
            <div className="actions mg" style={{ marginBottom: 12 }}>
              {Array.isArray(product?.Wishlists) &&
              product?.Wishlists?.length > 0 ? (
                <a
                  style={{ cursor: "pointer" }}
                  onClick={() => {
                    isLoggedIn
                      ? removeToWishlist(product?.Wishlists?.[0]?.id)
                      : toast.info("Please login first.", {
                          position: toast.POSITION.TOP_RIGHT,
                          theme: "colored",
                        });
                  }}
                >
                  <i
                    className="fa fa-heart"
                    style={{ color: "red", marginRight: 6 }}
                  ></i>{" "}
                  Wishlist
                </a>
              ) : (
                <a
                  style={{ cursor: "pointer" }}
                  onClick={() => {
                    isLoggedIn
                      ? addToWishlist({ ProductId: product?.id })
                      : toast.info("Please login first.", {
                          position: toast.POSITION.TOP_RIGHT,
                          theme: "colored",
                        });
                  }}
                >
                  <i
                    className="fa-regular fa-heart"
                    style={{ marginRight: 6 }}
                  ></i>{" "}
                  Wishlist
                </a>
              )}
            </div>

            {/* Return Policy */}
            {product?.returnPolicy && (
              <div
                style={{
                  background: "#f9f9f9",
                  border: "1px solid #eee",
                  borderRadius: 6,
                  padding: "10px 14px",
                  fontSize: 13,
                  color: "#555",
                  marginBottom: 12,
                }}
              >
                <i
                  className="fa-solid fa-rotate-left"
                  style={{ marginRight: 6 }}
                ></i>
                <strong>Return Policy:</strong> {product.returnPolicy}
              </div>
            )}

            {/* Share */}
            <div style={{ marginBottom: 6, fontWeight: 600, fontSize: 14 }}>
              Share Now
            </div>
            <div
              className="social-buttons mg"
              style={{ display: "flex", gap: 8 }}
            >
              <FacebookShareButton
                url={window.location.href}
                hashtag={`#${product?.name}`}
              >
                <FacebookIcon size={32} round />
              </FacebookShareButton>
              <LinkedinShareButton
                url={window.location.href}
                title={product?.name}
              >
                <LinkedinIcon size={32} round />
              </LinkedinShareButton>
              <PinterestShareButton
                media={displayImage}
                url={window.location.href}
              >
                <PinterestIcon size={32} round />
              </PinterestShareButton>
              <TwitterShareButton
                url={window.location.href}
                title={product?.name}
              >
                <TwitterIcon size={32} round />
              </TwitterShareButton>
              <WhatsappShareButton
                url={window.location.href}
                title={product?.name}
              >
                <WhatsappIcon size={32} round />
              </WhatsappShareButton>
            </div>
          </div>
        </div>

        {/* ===== Bottom Tabs ===== */}
        <div style={{ marginTop: 50, marginBottom: 80, width: "100%" }}>
          <div
            className="product-tabs-header"
            style={{
              display: "flex",
              borderBottom: "2px solid #eee",
              marginBottom: 24,
            }}
          >
            {["description", "benefits", "returnPolicy", "reviews"].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                style={{
                  padding: "10px 24px",
                  border: "none",
                  background: "none",
                  fontWeight: activeTab === tab ? 700 : 400,
                  borderBottom:
                    activeTab === tab
                      ? "3px solid #66A96B"
                      : "3px solid transparent",
                  cursor: "pointer",
                  fontSize: 14,
                  textTransform: "capitalize",
                  color: activeTab === tab ? "#66A96B" : "#555",
                }}
              >
                {tab === "returnPolicy"
                  ? "Return Policy"
                  : tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
          </div>

          {activeTab === "description" && (
            <div dangerouslySetInnerHTML={{ __html: product.description }} />
          )}
          {activeTab === "benefits" &&
            (benefits.length === 0 ? (
              <p style={{ color: "gray" }}>No benefits listed.</p>
            ) : (
              <ul style={{ paddingLeft: 20 }}>
                {benefits.map((b, i) => (
                  <li
                    key={i}
                    style={{ fontSize: 15, marginBottom: 8, color: "#444" }}
                  >
                    {b}
                  </li>
                ))}
              </ul>
            ))}
          {activeTab === "returnPolicy" &&
            (product?.returnPolicy ? (
              <p style={{ fontSize: 15, color: "#444" }}>
                {product.returnPolicy}
              </p>
            ) : (
              <p style={{ color: "gray" }}>No return policy listed.</p>
            ))}

          {activeTab === "reviews" && (
            <div style={{ textAlign: "left" }}>
              <h5 style={{ fontWeight: 700, marginBottom: 16 }}>Customer Reviews</h5>
              {reviews.length === 0 ? (
                <p style={{ color: "gray" }}>No reviews yet for this product. Be the first to buy and review it!</p>
              ) : (
                <div>
                  {/* Summary Card */}
                  <div style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 24,
                    padding: 16,
                    background: "#f9f9f9",
                    borderRadius: 8,
                    marginBottom: 24,
                    border: "1px solid #eee",
                    maxWidth: "400px"
                  }}>
                    <div>
                      <h2 style={{ fontSize: 44, fontWeight: 800, margin: 0, color: "#333" }}>{averageRating}</h2>
                      <div style={{ color: "#FFD700", fontSize: 20 }}>
                        {renderStars(averageRating)}
                      </div>
                      <div style={{ fontSize: 13, color: "gray", marginTop: 4 }}>Based on {totalReviews} reviews</div>
                    </div>
                    <div style={{ flexGrow: 1, borderLeft: "1px solid #ddd", paddingLeft: 20 }}>
                      {[5, 4, 3, 2, 1].map(stars => {
                        const count = reviews.filter(r => r.rating === stars).length;
                        const pct = totalReviews > 0 ? (count / totalReviews) * 100 : 0;
                        return (
                          <div key={stars} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, marginBottom: 2 }}>
                            <span style={{ width: 12 }}>{stars}</span>
                            <span>★</span>
                            <div style={{ flexGrow: 1, height: 6, background: "#eee", borderRadius: 3, overflow: "hidden" }}>
                              <div style={{ width: `${pct}%`, height: "100%", background: "#66A96B" }} />
                            </div>
                            <span style={{ width: 20, textAlign: "right" }}>{count}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Reviews List */}
                  <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                    {reviews.map((r) => (
                      <div key={r.id} style={{ borderBottom: "1px solid #eee", paddingBottom: 16 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 4 }}>
                          <strong style={{ fontSize: 14, color: "#333" }}>
                            {r.User ? `${r.User.firstName} ${r.User.lastName}` : "Customer"}
                          </strong>
                          <span style={{ fontSize: 11, color: "gray" }}>
                            {new Date(r.createdAt).toLocaleDateString("en-IN", { day: 'numeric', month: 'short', year: 'numeric' })}
                          </span>
                        </div>
                        <div style={{ color: "#FFD700", fontSize: 14, marginBottom: 6 }}>
                          {renderStars(r.rating)}
                        </div>
                        {r.comment && (
                          <p style={{ fontSize: 14, color: "#555", margin: 0, lineHeight: 1.5 }}>
                            {r.comment}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ✅ Lightbox — click main image or thumbnail to open */}
      {lightbox && (
        <div
          onClick={() => setLightbox(null)}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.90)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
            cursor: "zoom-out",
          }}
        >
          <button
            onClick={() => setLightbox(null)}
            style={{
              position: "absolute",
              top: 20,
              right: 28,
              background: "rgba(255,255,255,0.15)",
              border: "none",
              color: "white",
              fontSize: 32,
              cursor: "pointer",
              lineHeight: 1,
              width: 44,
              height: 44,
              borderRadius: "50%",
            }}
          >
            ×
          </button>

          {/* Thumbnail strip inside lightbox */}
          {allImages.length > 1 && (
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                position: "absolute",
                bottom: 20,
                left: "50%",
                transform: "translateX(-50%)",
                display: "flex",
                gap: 8,
              }}
            >
              {allImages.map((img) => (
                <div
                  key={img.id}
                  onClick={() => setLightbox(img.url)}
                  style={{
                    width: 56,
                    height: 56,
                    border:
                      lightbox === img.url
                        ? "2px solid #66A96B"
                        : "2px solid rgba(255,255,255,0.3)",
                    borderRadius: 6,
                    overflow: "hidden",
                    cursor: "pointer",
                    flexShrink: 0,
                  }}
                >
                  <img
                    src={img.url}
                    alt=""
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                </div>
              ))}
            </div>
          )}

          <img
            src={lightbox}
            alt="full size"
            onClick={(e) => e.stopPropagation()}
            style={{
              maxWidth: "85vw",
              maxHeight: "80vh",
              objectFit: "contain",
              borderRadius: 4,
            }}
          />
        </div>
      )}
    </div>
  );
};
